// KontractAI shared utility functions.
//
// String formatting, date helpers, and ID generation. These are used
// throughout the app and should not depend on React or component state.
// Anything that returns JSX belongs in components/, not here.

export const uid = (): string => Math.random().toString(36).substr(2, 9);

// RBAC helper: does this user have the given permission?
// Reads from user.permissions[] attached by /api/auth/login + /api/auth/me
// since 2026-04-21 (commit fe76d06). Returns false when permissions is
// missing (e.g., pre-migration session) so callers with a legacy role
// fallback can still authorize via the role string.
export const hasPermission = (user: any, key: string): boolean => {
  if (!user || !Array.isArray(user.permissions)) return false;
  return user.permissions.includes(key);
};

export const today = (): string => new Date().toISOString().split("T")[0];

export const daysAgo = (n: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
};

export const fmtDate = (d: string | Date | null | undefined): string => {
  if (!d) return "";
  let dateStr = String(d);
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) dateStr += "T12:00:00";
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}

// Map a snake_case DB status string to the human-readable UI status.
// Includes legacy DB values from previous schema migrations so old
// data still renders sensibly.
export function mapStatusFromDB(dbStatus: string): string {
  const map: Record<string, string> = {
    self_service_draft: "Self-Service Draft",
    pending_assignment: "Pending Assignment",
    pending_commops_assignment: "Pending CommOps Assignment",
    pending_legal_assignment: "Pending Legal Assignment",
    needs_more_information: "Needs More Information",
    commops_review: "CommOps Review",
    legal_review: "Legal Review",
    internal_review_pending: "Internal Review Pending",
    counterparty_draft_ready: "Counterparty Draft Ready",
    sent_to_client: "Counterparty Review Pending",
    counterparty_review_pending: "Counterparty Review Pending",
    approved_for_signature: "Approved for Signature",
    pending_signnow_send: "Pending SignNow Send",
    sent_for_signature: "Sent for Signature",
    fully_executed: "Fully Executed",
    cancelled: "Cancelled",
    // Legacy/renamed status mappings
    client_draft_ready: "Counterparty Draft Ready",
    client_review_pending: "Counterparty Review Pending",
    business_review: "CommOps Review",
    counterparty_review: "Counterparty Review Pending",
    legal_review_completed: "Approved for Signature",
    partially_executed: "Sent for Signature",
    stakeholder_feedback_pending: "Internal Review Pending",
    counterparty_redline_received: "Legal Review",
    negotiation_complete: "Approved for Signature",
    // Legacy DB values
    draft_intake: "Pending Assignment", submitted: "Pending Assignment", request_received: "Pending Assignment",
    assigned_to_lawyer: "Legal Review", under_review: "Legal Review",
    waiting_on_business: "CommOps Review", waiting_on_counterparty: "Counterparty Review Pending",
    internal_approval_pending: "Approved for Signature", closed: "Cancelled",
  };
  return map[dbStatus] || dbStatus;
}

// Map a UI status string back to the DB enum value. Falls back to a
// snake_case-ified version for unknown values.
export function mapStatusToDB(uiStatus: string): string {
  const map: Record<string, string> = {
    "Pending Assignment": "pending_assignment",
    "Pending CommOps Assignment": "pending_commops_assignment",
    "Pending Legal Assignment": "pending_legal_assignment",
    "Self-Service Draft": "self_service_draft",
    "Needs More Information": "needs_more_information",
    "CommOps Review": "commops_review",
    "Legal Review": "legal_review",
    "Internal Review Pending": "internal_review_pending",
    "Counterparty Draft Ready": "counterparty_draft_ready",
    "Sent to Client": "counterparty_review_pending",
    "Counterparty Review Pending": "counterparty_review_pending",
    "Approved for Signature": "approved_for_signature",
    "Pending SignNow Send": "pending_signnow_send",
    "Sent for Signature": "sent_for_signature",
    "Fully Executed": "fully_executed",
    "Cancelled": "cancelled",
  };
  return map[uiStatus] || uiStatus.toLowerCase().replace(/ /g, "_");
}

export function toTitleCase(str: string | null | undefined): string {
  if (!str) return str || '';
  const upperKeep = ['LLC', 'LP', 'LLP', 'PC', 'PLLC', 'PA', 'NA', 'INC', 'CO', 'CORP', 'LTD', 'PLC', 'AG', 'SA', 'NV', 'BV', 'SE', 'AB', 'OY', 'AS', 'SRL', 'GMBH', 'II', 'III', 'IV', 'USA', 'US'];
  const lowerKeep = ['a', 'an', 'the', 'and', 'or', 'of', 'to', 'in', 'for', 'on', 'at', 'by', 'with', 'from'];
  const hasLower = /[a-z]/.test(str);
  const hasUpper = /[A-Z]/.test(str);
  if (hasLower && hasUpper) return str;
  return str.split(/(\s+|[\/\-])/).map((word: string, index: number, arr: string[]) => {
    if (/^[\s\/\-]+$/.test(word)) return word;
    const stripped = word.replace(/[.,]/g, '');
    const suffix = word.slice(stripped.length);
    if (upperKeep.includes(stripped.toUpperCase())) return stripped.toUpperCase() + suffix;
    const prevNonSpace = arr.slice(0, index).filter((w: string) => !/^[\s\/\-]+$/.test(w));
    if (prevNonSpace.length > 0 && lowerKeep.includes(stripped.toLowerCase())) return stripped.toLowerCase() + suffix;
    return stripped.charAt(0).toUpperCase() + stripped.slice(1).toLowerCase() + suffix;
  }).join('');
}
